import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFlag } from '@rsos/flags';
import { KeywordMatches } from '../KeywordMatches';
import Keywords from '../Keywords';
import PoweredByHarmony from '../PoweredByHarmony';
import {
  LATEST_SUMMARY_INDEX,
  selectTranscriptionForCall,
} from '../transcriptionSlice';
import CopySummary from './CopySummary';
import {
  LinearGradientBox,
  ConicGradientBox,
  IncidentBox,
  IncidentHeader,
  IncidentContent,
  CopySummaryWrapper,
  PoweredByHarmonyWrapper,
} from './IncidentSummary.styles';

/**
 * Call audio summary component that is displayed in the event pane's transcript tab.
 * @param {String} callerId - caller ID associated with the incident summary
 */
const IncidentSummary = ({ callerId = '' }) => {
  // TODO: Toggle glow animation effect based on incident summary API request.
  const [isGlowVisible, setIsGlowVisible] = useState(true);

  const summaries = useSelector(
    state => selectTranscriptionForCall(callerId)(state).summaries,
  );

  const keywords = useSelector(
    state => selectTranscriptionForCall(callerId)(state).keywords,
  );

  const mostRecentSummary = useMemo(() => {
    if (summaries?.length) {
      return summaries[LATEST_SUMMARY_INDEX];
    }
    return null;
  }, [summaries]);

  // Use effect to update glow state when summary becomes final
  useEffect(() => {
    if (mostRecentSummary?.is_final) {
      setIsGlowVisible(false);
    }
  }, [mostRecentSummary]);

  const isKeywordSetsEnabled = useFlag('keyword_sets');

  if (!summaries?.length || !callerId) {
    return null;
  }

  const hasKeywords =
    !!keywords && Array.isArray(keywords) && keywords.length > 0;

  const hasSummaryText =
    mostRecentSummary?.summary && mostRecentSummary.summary.length > 0;

  return (
    <>
      {hasSummaryText && (
        <LinearGradientBox data-name="incident-summary-card">
          <ConicGradientBox isGlowVisible={isGlowVisible}>
            <IncidentBox>
              <IncidentHeader>
                <PoweredByHarmonyWrapper>
                  <PoweredByHarmony showStar title="Call Audio Summary" />
                </PoweredByHarmonyWrapper>
                <CopySummaryWrapper>
                  <CopySummary summaryText={mostRecentSummary.summary} />
                </CopySummaryWrapper>
              </IncidentHeader>
              <IncidentContent data-name="incident-content">
                {mostRecentSummary.summary}
              </IncidentContent>
            </IncidentBox>
          </ConicGradientBox>
        </LinearGradientBox>
      )}
      {!isKeywordSetsEnabled && hasKeywords && <Keywords keywords={keywords} />}
      {isKeywordSetsEnabled && <KeywordMatches />}
    </>
  );
};

export default IncidentSummary;
